import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import ReportSpotModal from "../ReportSpotModal";
import SpotDetailTabs from "../spot/SpotDetailTabs";
import SpotDetailView from "../spot/SpotDetailView";
import SpotDrawRouteModal from "../spot/SpotDrawRouteModal";
import { useSpotDetail } from "../../hooks/useSpotDetail";
import { writeSpotPopupTabsToUrl } from "../../utils/spotPopupUrl";

export default function MapEntityPopup({
    entity,
    onClose,
    onChanged,
}) {
    const { isAuthenticated } = useAuth();
    const [tabsMenuOpen, setTabsMenuOpen] = useState(false);
    const [drawModalOpen, setDrawModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("main");
    const [activePhotoTab, setActivePhotoTab] = useState("place");
    const [activeWeatherSubtab, setActiveWeatherSubtab] = useState("bite");

    const spotId = entity?.kind === "spot" ? entity.id : null;

    useEffect(() => {
        if (!entity?.id) return;
        setActiveTab(entity.initialTab || "main");
        setActivePhotoTab(entity.initialPhotoTab || "place");
        setActiveWeatherSubtab(entity.initialWeatherSubtab || "bite");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial tabs apply only when opening another spot
    }, [entity?.id]);

    useEffect(() => {
        if (!entity?.id) return;
        writeSpotPopupTabsToUrl(entity.id, {
            activeTab,
            activePhotoTab,
            activeWeatherSubtab,
        });
    }, [entity?.id, activeTab, activePhotoTab, activeWeatherSubtab]);

    const controlledTab = useMemo(
        () => ({ activeTab, setActiveTab }),
        [activeTab],
    );
    const controlledPhotoTab = useMemo(
        () => ({ photoType: activePhotoTab, setPhotoType: setActivePhotoTab }),
        [activePhotoTab],
    );
    const controlledWeatherType = useMemo(
        () => ({ weatherType: activeWeatherSubtab, setWeatherType: setActiveWeatherSubtab }),
        [activeWeatherSubtab],
    );

    const detail = useSpotDetail(spotId, {
        countView: true,
        weatherLazy: true,
        initialSpot: entity?.payload,
        controlledTab,
        controlledPhotoTab,
        controlledWeatherType,
        onEngagementChange: onChanged,
    });

    useEffect(() => {
        if (!entity) return undefined;
        const handleEsc = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [entity, onClose]);

    useEffect(() => {
        setTabsMenuOpen(false);
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== "reports") {
            detail.setCommentFormOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to tab changes
    }, [activeTab]);

    if (!entity) return null;

    const spotPagePath = entity.kind === "spot" ? `/spots/${entity.id}` : null;
    const spotEditSuggestionPath = entity.kind === "spot" ? `/spots/${entity.id}/edit-suggestion` : null;

    const requireAuthForEditSuggestion = (event) => {
        if (isAuthenticated || !spotEditSuggestionPath) return;
        event.preventDefault();
        onClose?.();
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: {
                nextPath: spotEditSuggestionPath,
                title: "Необходима авторизация",
            },
        }));
    };

    const title = detail.mapTitle || entity.title || `Точка #${entity.id}`;

    return (
        <>
            {detail.reportModalOpen && (
                <ReportSpotModal
                    entityType="spot"
                    entityId={entity.id}
                    onClose={() => detail.setReportModalOpen(false)}
                />
            )}
            {!detail.reportModalOpen && (
                <>
                    <div
                        className="entity-popup-backdrop"
                        onClick={onClose}
                        aria-hidden="true"
                    />
                    <div className="entity-popup entity-popup--glass" onClick={(event) => event.stopPropagation()}>
                        <div className="entity-popup__panel">
                            <div className="entity-popup__head">
                                <h3 className="spot-glass-title">{title}</h3>
                                <button type="button" className="entity-popup__close" onClick={onClose} aria-label="Закрыть">
                                    ×
                                </button>
                            </div>
                            <div className="entity-popup__scroll spot-glass-panel">
                                {detail.loading && !detail.spot && <p className="page-status-message">Загрузка данных...</p>}
                                {detail.message && !detail.spot && <p className="form-message">{detail.message}</p>}
                                {!detail.loading && !detail.message && !detail.spot && (
                                    <p className="page-status-message">Данные для этого объекта пока недоступны.</p>
                                )}

                                {detail.spot && (
                                    <div className="entity-popup__content">
                                        <SpotDetailTabs
                                            variant="page"
                                            activeTab={detail.activeTab}
                                            onTabChange={detail.setActiveTab}
                                            tabsMenuOpen={tabsMenuOpen}
                                            setTabsMenuOpen={setTabsMenuOpen}
                                        />
                                        <SpotDetailView
                                            detail={detail}
                                            variant="popup"
                                            panelClassName="spot-page__panel"
                                            commentsMountHidden
                                            commentsLoading={detail.loading && !detail.comments?.length}
                                            onClose={onClose}
                                            onDrawRoute={() => setDrawModalOpen(true)}
                                            extraMainActions={(
                                                <>
                                                    {spotPagePath && (
                                                        <Link to={spotPagePath} className="btn-secondary" onClick={onClose}>
                                                            Перейти на страницу
                                                        </Link>
                                                    )}
                                                    {spotEditSuggestionPath && (
                                                        <Link
                                                            to={spotEditSuggestionPath}
                                                            className="btn-secondary"
                                                            onClick={requireAuthForEditSuggestion}
                                                        >
                                                            Предложить изменения
                                                        </Link>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
            {drawModalOpen && detail.spot && (
                <SpotDrawRouteModal
                    spotId={entity.id}
                    latitude={detail.spot.latitude}
                    longitude={detail.spot.longitude}
                    onClose={() => setDrawModalOpen(false)}
                    onSaved={() => detail.reload()}
                />
            )}
        </>
    );
}
